import React, { useState, useEffect, useCallback, useRef } from 'react';
import { passwordsAPI } from '../services/api';

const defaultForm = { title: '', username: '', password: '', url: '', category: '', notes: '' };
const defaultSQ = { question: '', answer: '' };
const AUTO_HIDE_SECONDS = 30;

// ── PIN Lock Screen ──────────────────────────────────────────────
function PinScreen({ onUnlock, hasPin, onPinSet }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settingPin] = useState(!hasPin);
  const [confirmDigits, setConfirmDigits] = useState(['', '', '', '', '', '']);
  const [confirmStep, setConfirmStep] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => { inputRefs.current[0]?.focus(); }, [settingPin]);

  const handleDigit = (index, value, arr, setArr) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...arr];
    next[index] = value;
    setArr(next);
    setError('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (!value && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handleKeyDown = (e, index, arr, setArr) => {
    if (e.key === 'Backspace' && !arr[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const pinValue = (arr) => arr.join('');

  const handleSubmit = async () => {
    const pin = pinValue(digits);
    if (pin.length < 6) { setError('Enter all 6 digits'); return; }

    if (settingPin) {
      if (!confirmStep) {
        setConfirmStep(true);
        setConfirmDigits(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
        return;
      }
      const confirm = pinValue(confirmDigits);
      if (pin !== confirm) {
        setError('PINs do not match. Try again.');
        setConfirmStep(false);
        setDigits(['', '', '', '', '', '']);
        setConfirmDigits(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
        return;
      }
      setLoading(true);
      try {
        await passwordsAPI.pinSet(pin);
        onPinSet();
        onUnlock();
      } catch { setError('Failed to set PIN'); }
      finally { setLoading(false); }
    } else {
      setLoading(true);
      try {
        await passwordsAPI.pinVerify(pin);
        onUnlock();
      } catch (err) {
        setError(err.response?.data?.error || 'Incorrect PIN');
        setDigits(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 50);
      } finally { setLoading(false); }
    }
  };

  const activeDigits = confirmStep ? confirmDigits : digits;
  const setActiveDigits = confirmStep ? setConfirmDigits : setDigits;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '60vh', padding: '32px 16px'
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
        borderRadius: '16px', padding: '40px 32px', maxWidth: '360px', width: '100%',
        textAlign: 'center', boxShadow: 'var(--shadow-elevated)'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
        <h3 style={{ marginBottom: '8px', fontSize: '20px' }}>
          {settingPin ? (confirmStep ? 'Confirm your PIN' : 'Set a 6-digit PIN') : 'Enter your PIN'}
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '28px' }}>
          {settingPin
            ? confirmStep
              ? 'Re-enter your PIN to confirm'
              : 'This PIN protects your Password Manager'
            : 'Enter your PIN to access passwords'}
        </p>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '24px' }}>
          {activeDigits.map((d, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={e => handleDigit(i, e.target.value, activeDigits, setActiveDigits)}
              onKeyDown={e => handleKeyDown(e, i, activeDigits, setActiveDigits)}
              onFocus={e => e.target.select()}
              style={{
                width: '44px', height: '52px', textAlign: 'center', fontSize: '22px',
                borderRadius: '10px', border: `2px solid ${d ? 'var(--accent-coral)' : 'var(--border-color)'}`,
                background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                outline: 'none', transition: 'border-color 0.15s'
              }}
            />
          ))}
        </div>

        {error && (
          <div style={{
            marginBottom: '16px', padding: '10px 14px', borderRadius: '8px',
            background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '13px'
          }}>
            {error}
          </div>
        )}

        <button onClick={handleSubmit} disabled={loading || pinValue(activeDigits).length < 6}
          className="btn btn-primary" style={{ width: '100%', padding: '12px', fontSize: '15px' }}>
          {loading ? '...' : settingPin ? (confirmStep ? 'Confirm PIN' : 'Next') : 'Unlock'}
        </button>
      </div>
    </div>
  );
}

// ── Auto-hide timer bar ──────────────────────────────────────────
function TimerBar({ seconds, total }) {
  const pct = (seconds / total) * 100;
  return (
    <div style={{ height: '3px', background: 'var(--border-color)', borderRadius: '2px', overflow: 'hidden', marginTop: '4px' }}>
      <div style={{
        height: '100%', width: `${pct}%`,
        background: pct > 50 ? 'var(--accent-coral)' : pct > 20 ? '#f59e0b' : '#ef4444',
        transition: 'width 1s linear, background 0.5s'
      }} />
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────
function PasswordManager() {
  const [unlocked, setUnlocked] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [pinLoading, setPinLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [revealed, setRevealed] = useState({});
  const [revealCountdowns, setRevealCountdowns] = useState({});
  const [copied, setCopied] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [formTab, setFormTab] = useState('credentials');
  const [securityQuestions, setSecurityQuestions] = useState([{ ...defaultSQ }]);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [showChangePIN, setShowChangePIN] = useState(false);

  const countdownRef = useRef({});

  useEffect(() => {
    passwordsAPI.pinStatus()
      .then(res => setHasPin(res.data.hasPin))
      .catch(() => {})
      .finally(() => setPinLoading(false));
  }, []);

  useEffect(() => {
    if (unlocked) { fetchEntries(); }
  }, [unlocked]);

  const fetchEntries = async () => {
    try {
      const res = await passwordsAPI.getAll();
      setEntries(res.data);
    } catch (err) {
      console.error('Error fetching passwords:', err);
    } finally {
      setLoading(false);
    }
  };

  const startAutoHide = useCallback((id) => {
    // Clear existing timer
    if (countdownRef.current[id]) clearInterval(countdownRef.current[id]);

    setRevealCountdowns(c => ({ ...c, [id]: AUTO_HIDE_SECONDS }));

    countdownRef.current[id] = setInterval(() => {
      setRevealCountdowns(c => {
        const next = (c[id] || 0) - 1;
        if (next <= 0) {
          clearInterval(countdownRef.current[id]);
          delete countdownRef.current[id];
          setRevealed(r => { const n = { ...r }; delete n[id]; return n; });
          setRevealCountdowns(cc => { const n = { ...cc }; delete n[id]; return n; });
          return { ...c, [id]: 0 };
        }
        return { ...c, [id]: next };
      });
    }, 1000);
  }, []);

  const handleReveal = async (entry) => {
    if (revealed[entry.id]) {
      clearInterval(countdownRef.current[entry.id]);
      delete countdownRef.current[entry.id];
      setRevealed(r => { const n = { ...r }; delete n[entry.id]; return n; });
      setRevealCountdowns(c => { const n = { ...c }; delete n[entry.id]; return n; });
      return;
    }
    try {
      const res = await passwordsAPI.reveal(entry.id);
      setRevealed(r => ({ ...r, [entry.id]: res.data.password }));
      startAutoHide(entry.id);
      passwordsAPI.logAction(entry.id, 'reveal').catch(() => {});
    } catch (err) {
      console.error('Error revealing password:', err);
    }
  };

  const handleCopy = async (id, text, isPassword, entry) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
      if (isPassword && entry) {
        passwordsAPI.logAction(entry.id, 'copy').catch(() => {});
      }
    } catch {
      alert('Could not copy to clipboard');
    }
  };

  const handleCopyPasswordDirect = async (entry) => {
    try {
      const res = await passwordsAPI.reveal(entry.id);
      await navigator.clipboard.writeText(res.data.password);
      setCopied(`cp-${entry.id}`);
      setTimeout(() => setCopied(null), 2000);
      passwordsAPI.logAction(entry.id, 'copy').catch(() => {});
    } catch {
      alert('Could not copy password');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let id = editingId;
      if (editingId) { await passwordsAPI.update(editingId, formData); }
      else { const res = await passwordsAPI.create(formData); id = res.data.id; }
      const validSQs = securityQuestions.filter(q => q.question.trim());
      if (id) await passwordsAPI.saveSecurityQuestions(id, validSQs);
      fetchEntries();
      resetForm();
    } catch (err) {
      console.error('Error saving entry:', err);
      alert('Failed to save entry');
    }
  };

  const handleEdit = async (entry) => {
    setFormData({ title: entry.title, username: entry.username || '', password: '', url: entry.url || '', category: entry.category || '', notes: entry.notes || '' });
    setEditingId(entry.id);
    setFormTab('credentials');
    setSecurityQuestions([{ ...defaultSQ }]);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      const res = await passwordsAPI.getSecurityQuestions(entry.id);
      setSecurityQuestions(res.data.length > 0 ? res.data : [{ ...defaultSQ }]);
    } catch { /* non-fatal */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await passwordsAPI.delete(id); fetchEntries(); }
    catch (err) { console.error('Error deleting entry:', err); }
  };

  const loadAuditLog = async () => {
    try {
      const res = await passwordsAPI.getAuditLog();
      setAuditLog(res.data);
      setShowAudit(true);
    } catch (err) {
      console.error('Error fetching audit log:', err);
    }
  };

  const resetForm = () => { setFormData(defaultForm); setEditingId(null); setShowForm(false); setShowFormPassword(false); setFormTab('credentials'); setSecurityQuestions([{ ...defaultSQ }]); };

  const categories = [...new Set(entries.map(e => e.category).filter(Boolean))].sort();

  const filtered = entries.filter(e => {
    if (filterCategory && e.category !== filterCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.title.toLowerCase().includes(q) || (e.username || '').toLowerCase().includes(q) || (e.url || '').toLowerCase().includes(q);
    }
    return true;
  });

  const grouped = filtered.reduce((acc, entry) => {
    const key = entry.category || 'Uncategorized';
    if (!acc[key]) acc[key] = [];
    acc[key].push(entry);
    return acc;
  }, {});

  if (pinLoading) return <div className="loading"><div className="spinner"></div></div>;

  // Show PIN screen if not unlocked
  if (!unlocked) {
    if (showChangePIN) {
      return (
        <div className="container fade-in">
          <div className="page-header">
            <div><h2>Change PIN</h2><p>Set a new 6-digit PIN</p></div>
            <button onClick={() => setShowChangePIN(false)} className="btn btn-secondary">Cancel</button>
          </div>
          <PinScreen
            hasPin={false}
            onUnlock={() => { setShowChangePIN(false); setUnlocked(true); }}
            onPinSet={() => setHasPin(true)}
          />
        </div>
      );
    }
    return (
      <div className="container fade-in">
        <div className="page-header">
          <div><h2>Password Manager</h2><p>Securely store family account credentials</p></div>
        </div>
        <PinScreen
          hasPin={hasPin}
          onUnlock={() => setUnlocked(true)}
          onPinSet={() => setHasPin(true)}
        />
      </div>
    );
  }

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Password Manager</h2>
          <p>Securely store family account credentials</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={loadAuditLog} className="btn btn-secondary" style={{ fontSize: '13px' }}>
            Audit Log
          </button>
          <button onClick={() => { setUnlocked(false); setRevealed({}); setRevealCountdowns({}); }}
            className="btn btn-secondary" style={{ fontSize: '13px' }}>
            🔒 Lock
          </button>
          <button onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }} className="btn btn-primary">
            {showForm ? 'Cancel' : '+ Add Entry'}
          </button>
        </div>
      </div>

      {/* Audit log modal */}
      {showAudit && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
        }}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '12px', width: '100%', maxWidth: '560px',
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-elevated)'
          }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Access Audit Log</h3>
              <button onClick={() => setShowAudit(false)} className="btn btn-secondary btn-sm">Close</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {auditLog.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>No activity yet</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)' }}>
                      <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Entry</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Action</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>User</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map(log => (
                      <tr key={log.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '10px 16px', fontWeight: 500 }}>{log.password_title}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{
                            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
                            background: log.action === 'copy' ? 'rgba(14,188,212,0.12)' : 'rgba(139,92,246,0.12)',
                            color: log.action === 'copy' ? 'var(--accent-coral)' : '#8b5cf6'
                          }}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', color: 'var(--text-secondary)' }}>{log.user_name || log.username}</td>
                        <td style={{ padding: '10px 16px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(log.accessed_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card card-table">
        {showForm && (
          <div className="form-section">
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px' }}>
                {editingId ? 'Edit Entry' : 'New Entry'}
              </h4>
              <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '8px', padding: '3px', width: '100%' }}>
                {['credentials', 'security-questions'].map(tab => (
                  <button key={tab} type="button"
                    onClick={() => setFormTab(tab)}
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                      fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s',
                      background: formTab === tab ? 'var(--bg-card)' : 'transparent',
                      color: formTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
                      boxShadow: formTab === tab ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
                      whiteSpace: 'nowrap',
                    }}>
                    {tab === 'credentials' ? 'Credentials' : 'Security Questions'}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* ── Credentials tab ── */}
              {formTab === 'credentials' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Title *</label>
                      <input type="text" className="form-input" value={formData.title} required
                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g. Netflix, Bank Login" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Username / Email</label>
                      <input type="text" className="form-input" value={formData.username}
                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                        placeholder="username or email" autoComplete="off" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{editingId ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showFormPassword ? 'text' : 'password'} className="form-input"
                          value={formData.password}
                          onChange={e => setFormData({ ...formData, password: e.target.value })}
                          required={!editingId} placeholder="••••••••" autoComplete="new-password"
                          style={{ paddingRight: '44px' }} />
                        <button type="button" onClick={() => setShowFormPassword(v => !v)}
                          style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}>
                          {showFormPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Website URL</label>
                      <input type="text" className="form-input" value={formData.url}
                        onChange={e => setFormData({ ...formData, url: e.target.value })}
                        placeholder="https://..." />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Category</label>
                      <input type="text" className="form-input" value={formData.category}
                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                        placeholder="e.g. Streaming, Banking" list="pw-cat" />
                      <datalist id="pw-cat">{categories.map(c => <option key={c} value={c} />)}</datalist>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Notes</label>
                      <input type="text" className="form-input" value={formData.notes}
                        onChange={e => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Optional notes" />
                    </div>
                  </div>
                </>
              )}

              {/* ── Security Questions tab ── */}
              {formTab === 'security-questions' && (
                <div style={{ marginBottom: '16px' }}>
                  {securityQuestions.map((sq, i) => (
                    <div key={i} style={{
                      background: 'var(--bg-secondary)', borderRadius: '8px', padding: '14px 16px',
                      marginBottom: '10px', border: '1px solid var(--border-subtle)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          Question {i + 1}
                        </span>
                        {securityQuestions.length > 1 && (
                          <button type="button"
                            onClick={() => setSecurityQuestions(qs => qs.filter((_, idx) => idx !== i))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '12px', padding: '2px 6px' }}>
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="form-group" style={{ marginBottom: '8px' }}>
                        <label className="form-label">Question</label>
                        <input type="text" className="form-input"
                          value={sq.question}
                          onChange={e => setSecurityQuestions(qs => qs.map((q, idx) => idx === i ? { ...q, question: e.target.value } : q))}
                          placeholder="e.g. What was your first pet's name?" />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Answer</label>
                        <input type="text" className="form-input"
                          value={sq.answer}
                          onChange={e => setSecurityQuestions(qs => qs.map((q, idx) => idx === i ? { ...q, answer: e.target.value } : q))}
                          placeholder="Answer" autoComplete="off" />
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setSecurityQuestions(qs => [...qs, { ...defaultSQ }])}>
                    + Add Question
                  </button>
                </div>
              )}

              <div className="form-row">
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '16px' }}>
                  <div className="flex gap-1">
                    <button type="submit" className="btn btn-success">{editingId ? 'Update' : 'Add'}</button>
                    <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Search + filter */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" className="form-input" placeholder="Search entries..." value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '160px', padding: '7px 10px', fontSize: '13px' }} />
          <select className="form-select" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            style={{ minWidth: '140px', fontSize: '13px' }}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span style={{ fontSize: '13px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}
          </span>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔐</div>
            <p className="empty-state-text">{entries.length === 0 ? 'No entries yet' : 'No entries match your search'}</p>
            {entries.length === 0 && <button onClick={() => setShowForm(true)} className="btn btn-primary">Add Your First Entry</button>}
          </div>
        ) : (
          <div>
            {Object.entries(grouped).sort(([a], [b]) => a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b)).map(([category, catEntries]) => (
              <div key={category}>
                <div style={{
                  padding: '8px 16px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.08em', color: 'var(--text-muted)', background: 'var(--bg-secondary)',
                  borderBottom: '1px solid var(--border-color)', borderTop: '1px solid var(--border-color)'
                }}>
                  {category} <span style={{ fontWeight: 400 }}>({catEntries.length})</span>
                </div>
                {catEntries.map(entry => (
                  <div key={entry.id} style={{
                    padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
                    display: 'flex', alignItems: 'flex-start', gap: '12px'
                  }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
                      background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '18px'
                    }}>🔐</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                        {entry.title}
                        {entry.url && (
                          <a href={entry.url} target="_blank" rel="noopener noreferrer"
                            style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--accent-coral)' }}>↗</a>
                        )}
                      </div>
                      {entry.username && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{entry.username}</span>
                          <button onClick={() => handleCopy(`u-${entry.id}`, entry.username, false, null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px',
                              color: copied === `u-${entry.id}` ? '#22c55e' : 'var(--text-muted)', padding: '0 4px' }}>
                            {copied === `u-${entry.id}` ? '✓ Copied' : 'Copy'}
                          </button>
                        </div>
                      )}

                      {/* Password row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-secondary)',
                          letterSpacing: revealed[entry.id] ? 'normal' : '3px', minWidth: '80px'
                        }}>
                          {revealed[entry.id] || '••••••'}
                        </span>
                        <button onClick={() => handleReveal(entry)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px',
                            color: 'var(--text-muted)', padding: '0 4px' }}>
                          {revealed[entry.id] ? '🙈 Hide' : '👁️ Show'}
                        </button>
                        {revealed[entry.id] ? (
                          <button onClick={() => handleCopy(`p-${entry.id}`, revealed[entry.id], true, entry)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px',
                              color: copied === `p-${entry.id}` ? '#22c55e' : 'var(--text-muted)', padding: '0 4px' }}>
                            {copied === `p-${entry.id}` ? '✓ Copied' : '📋 Copy'}
                          </button>
                        ) : (
                          <button onClick={() => handleCopyPasswordDirect(entry)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px',
                              color: copied === `cp-${entry.id}` ? '#22c55e' : 'var(--text-muted)', padding: '0 4px' }}>
                            {copied === `cp-${entry.id}` ? '✓ Copied' : '📋 Copy'}
                          </button>
                        )}
                      </div>

                      {/* Auto-hide countdown bar */}
                      {revealed[entry.id] && revealCountdowns[entry.id] !== undefined && (
                        <div style={{ marginTop: '6px', maxWidth: '200px' }}>
                          <TimerBar seconds={revealCountdowns[entry.id]} total={AUTO_HIDE_SECONDS} />
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            Hides in {revealCountdowns[entry.id]}s
                          </span>
                        </div>
                      )}

                      {entry.notes && (
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{entry.notes}</div>
                      )}
                    </div>
                    <div className="list-item-actions" style={{ flexShrink: 0 }}>
                      <button onClick={() => handleEdit(entry)} className="btn btn-secondary btn-sm">Edit</button>
                      <button onClick={() => handleDelete(entry.id)} className="btn btn-danger btn-sm">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PasswordManager;
